import React from "react";
import {
  partnerPortfolioService,
  PORTFOLIO_TEXTFELDER,
  type PortfolioDaten,
  type PortfolioEintrag,
  type PortfolioSicht,
  type PortfolioKlasse,
  type PortfolioProdukt,
  type PortfolioPartner,
} from "../../services/partnerPortfolioService";
import { gruppierePortfolio } from "./portfolioModel";
import { bestaetigen } from "../redesign/bestaetigen";
import "./partnerPortfolio.css";
type Entwurf = Omit<PortfolioEintrag, "id"> & {
  id?: number;
  key: string;
  stand?: string;
};
const daten = (row: Entwurf): PortfolioDaten =>
  ({
    ...Object.fromEntries(PORTFOLIO_TEXTFELDER.map(([key]) => [key, row[key]])),
    produktpartnerContactId: row.produktpartnerContactId,
    insuranceProductId: row.insuranceProductId,
    sort: row.sort,
  }) as PortfolioDaten;
const signatur = (row: Entwurf) => JSON.stringify(daten(row));
const entwurf = (row: PortfolioEintrag): Entwurf => {
  const e = { ...row, key: `db-${row.id}` };
  return { ...e, stand: signatur(e) };
};
const meldung = (e: unknown) =>
  e instanceof Error ? e.message : "Portfolio konnte nicht gespeichert werden";
export interface PartnerPortfolioHandle {
  speichern: () => Promise<boolean>;
  verwerfen: () => void;
  geaendert: () => boolean;
}
export const PartnerPortfolio = React.forwardRef<
  PartnerPortfolioHandle,
  {
    contactId: number;
    nurLesen: boolean;
    onKontaktOeffnen: (id: number) => void;
    onUebersicht: () => void;
  }
>(function PartnerPortfolio(
  { contactId, nurLesen, onKontaktOeffnen, onUebersicht },
  ref,
) {
  const [, render] = React.useReducer((n) => n + 1, 0);
  const state = React.useRef({
    geladen: false,
    fehler: "",
    speichert: false,
    sicht: {
      bearbeitbar: false,
      kooperation: false,
      eintraege: [],
    } as PortfolioSicht,
    zeilen: [] as Entwurf[],
    bestand: [] as Entwurf[],
    entfernt: [] as number[],
    klassen: [] as PortfolioKlasse[],
    produkte: [] as PortfolioProdukt[],
    partner: [] as PortfolioPartner[],
  });
  const generation = React.useRef(0);
  const zaehler = React.useRef(0);
  const [versuch, setVersuch] = React.useState(0);
  const [nurAngebotene, setNurAngebotene] = React.useState(false);
  const [zu, setZu] = React.useState<Set<number>>(new Set());
  React.useEffect(() => {
    const g = ++generation.current;
    const abort = new AbortController();
    const x = {
      ...state.current,
      geladen: false,
      speichert: false,
      fehler: "",
      zeilen: [] as Entwurf[],
      bestand: [] as Entwurf[],
      entfernt: [] as number[],
    };
    state.current = x;
    render();
    void Promise.all([
      partnerPortfolioService.list(contactId, abort.signal),
      partnerPortfolioService.katalog(abort.signal),
      partnerPortfolioService.partner(abort.signal),
    ])
      .then(([sicht, katalog, partner]) => {
        if (g !== generation.current || abort.signal.aborted) return;
        const zeilen = sicht.eintraege.map(entwurf);
        Object.assign(x, {
          sicht,
          zeilen,
          bestand: zeilen.map((z) => ({ ...z })),
          ...katalog,
          partner,
          geladen: true,
        });
        render();
      })
      .catch((e) => {
        if (g !== generation.current || abort.signal.aborted) return;
        x.fehler = meldung(e);
        render();
      });
    return () => {
      abort.abort();
      generation.current++;
    };
  }, [contactId, versuch]);
  const dirty = () =>
    state.current.entfernt.length > 0 ||
    state.current.zeilen.some(
      (z) => z.bearbeitbar && (!z.id || signatur(z) !== z.stand),
    );
  React.useImperativeHandle(
    ref,
    () => ({
      geaendert: dirty,
      verwerfen: () => {
        const x = state.current;
        if (x.speichert) return;
        x.zeilen = x.bestand.map((z) => ({ ...z }));
        x.entfernt = [];
        x.fehler = "";
        render();
      },
      speichern: async () => {
        const x = state.current;
        if (!dirty()) return true;
        if (nurLesen || !x.sicht.bearbeitbar || !x.geladen || x.speichert) {
          x.fehler = "Portfolio ist derzeit nicht bearbeitbar.";
          render();
          return false;
        }
        x.speichert = true;
        x.fehler = "";
        render();
        const fehler: string[] = [];
        const g = generation.current;
        try {
          for (const z of x.zeilen) {
            if (!z.bearbeitbar || (z.id && z.stand === signatur(z))) continue;
            if (g !== generation.current) return false;
            try {
              const result = z.id
                ? await partnerPortfolioService.update(
                    contactId,
                    z.id,
                    daten(z),
                  )
                : await partnerPortfolioService.create(contactId, daten(z));
              if (g !== generation.current) return false;
              z.id = result.id;
              z.stand = signatur(z);
              x.bestand = x.bestand.filter((b) => b.key !== z.key);
              x.bestand.push({ ...z });
            } catch (e) {
              fehler.push(meldung(e));
            }
          }
          for (const id of [...x.entfernt]) {
            if (g !== generation.current) return false;
            try {
              await partnerPortfolioService.remove(contactId, id);
              if (g !== generation.current) return false;
              x.entfernt = x.entfernt.filter((n) => n !== id);
              x.bestand = x.bestand.filter((b) => b.id !== id);
            } catch (e) {
              fehler.push(meldung(e));
            }
          }
          x.fehler = fehler.join("\n");
          return fehler.length === 0;
        } finally {
          x.speichert = false;
          if (g === generation.current) render();
        }
      },
    }),
    [contactId, nurLesen],
  );
  const x = state.current;
  const schreibbar =
    x.geladen && x.sicht.bearbeitbar && !nurLesen && !x.speichert;
  const aendern = (z: Entwurf, patch: Partial<PortfolioDaten>) => {
    if (!schreibbar || !z.bearbeitbar) return;
    Object.assign(z, patch);
    render();
  };
  const hinzufuegen = (p: PortfolioProdukt) => {
    if (!schreibbar || p.active === false) return;
    x.zeilen.push({
      ...Object.fromEntries(PORTFOLIO_TEXTFELDER.map(([key]) => [key, ""])),
      id: undefined,
      key: `neu-${++zaehler.current}`,
      produktpartnerContactId: x.sicht.kooperation ? 0 : contactId,
      insuranceProductId: p.id,
      insuranceProductName: p.name,
      abrechnungContactId: contactId,
      abrechnungName: "",
      produktpartnerName: "",
      bearbeitbar: true,
      sort: Math.min(10000, x.zeilen.length),
    } as Entwurf);
    render();
  };
  const entfernen = async (z: Entwurf) => {
    if (!schreibbar || !z.bearbeitbar) return;
    const g = generation.current;
    if (
      !(await bestaetigen({
        titel: "Produkt entfernen?",
        text: "Der Portfolioeintrag wird beim Speichern in den Papierkorb gelegt.",
        ton: "danger",
      }))
    )
      return;
    if (g !== generation.current || state.current.speichert) return;
    if (z.id) x.entfernt.push(z.id);
    x.zeilen = x.zeilen.filter((e) => e.key !== z.key);
    render();
  };
  const gruppen = gruppierePortfolio(
    x.klassen,
    x.produkte,
    [...x.zeilen].sort(
      (a, b) =>
        Number(b.bearbeitbar) - Number(a.bearbeitbar) || a.sort - b.sort,
    ),
    nurAngebotene,
  );
  return (
    <section className="partnerportfolio" aria-label="Produktportfolio">
      <div className="portfolio-toolbar">
        <label>
          <input
            type="checkbox"
            checked={nurAngebotene}
            onChange={(e) => setNurAngebotene(e.target.checked)}
          />{" "}
          Nur angebotene
        </label>
        <button type="button" className="btn" onClick={onUebersicht}>
          Gesamtübersicht
        </button>
      </div>
      {x.fehler && (
        <p role="alert" className="portfolio-fehler">
          {x.fehler}
        </p>
      )}
      {!x.geladen ? (
        <>
          <p role="status">Produktportfolio wird geladen …</p>
          {x.fehler && (
            <button
              type="button"
              className="btn"
              onClick={() => setVersuch((v) => v + 1)}
            >
              Erneut laden
            </button>
          )}
        </>
      ) : (
        <>
          {!x.sicht.bearbeitbar && (
            <p>
              Hier sehen Sie die Produkte, die Kooperationspartner für diese
              Gesellschaft führen. Direkte Einträge bleiben bei einer Umstellung
              erhalten.
            </p>
          )}
          {x.sicht.bearbeitbar && nurLesen && (
            <p>Zum Bearbeiten das Schloss der Akte öffnen.</p>
          )}
          {schreibbar && (
            <p>Änderungen werden mit dem Schloss der Akte gespeichert.</p>
          )}
          {x.speichert && <p role="status">Portfolio wird gespeichert …</p>}
          {gruppen.length === 0 && (
            <p>
              Keine {nurAngebotene ? "angebotenen" : "aktiven"} Vertragsarten
              vorhanden.
            </p>
          )}
          {gruppen.map((gruppe) => (
            <div className="card portfolio-gruppe" key={gruppe.id}>
              <button
                type="button"
                className="portfolio-gruppenkopf"
                aria-expanded={!zu.has(gruppe.id)}
                onClick={() =>
                  setZu((alt) => {
                    const neu = new Set(alt);
                    neu.has(gruppe.id)
                      ? neu.delete(gruppe.id)
                      : neu.add(gruppe.id);
                    return neu;
                  })
                }
              >
                {gruppe.name}
              </button>
              {!zu.has(gruppe.id) && (
                <div className="portfolio-scroll">
                  <table className="kt">
                    <thead>
                      <tr>
                        <th>Vertragsart gemäß KMVS</th>
                        {x.sicht.kooperation && <th>Produktpartner</th>}
                        {PORTFOLIO_TEXTFELDER.map(([key, label]) => (
                          <th key={key}>{label}</th>
                        ))}
                        <th>Herkunft / Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gruppe.arten.map(({ produkt, eintraege }) => (
                        <React.Fragment key={produkt.id}>
                          {eintraege.map((z) => (
                            <tr key={z.key}>
                              <td>
                                {produkt.name}
                                {produkt.active === false && (
                                  <small> (inaktiv)</small>
                                )}
                              </td>
                              {x.sicht.kooperation && (
                                <td>
                                  {schreibbar && z.bearbeitbar ? (
                                    <select
                                      aria-label="Produktpartner"
                                      className="inp"
                                      value={z.produktpartnerContactId || ""}
                                      onChange={(e) =>
                                        aendern(z, {
                                          produktpartnerContactId: Number(
                                            e.target.value,
                                          ),
                                        })
                                      }
                                    >
                                      <option value="">Bitte wählen</option>
                                      {x.partner.map((p) => (
                                        <option key={p.id} value={p.id}>
                                          {p.name}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <button
                                      type="button"
                                      className="portfolio-link"
                                      onClick={() =>
                                        onKontaktOeffnen(
                                          z.produktpartnerContactId,
                                        )
                                      }
                                    >
                                      {x.partner.find(
                                        (p) =>
                                          p.id === z.produktpartnerContactId,
                                      )?.name || z.produktpartnerName}
                                    </button>
                                  )}
                                </td>
                              )}
                              {PORTFOLIO_TEXTFELDER.map(([key, label]) => (
                                <td key={key}>
                                  {schreibbar && z.bearbeitbar ? (
                                    <input
                                      className="inp"
                                      aria-label={label}
                                      maxLength={2000}
                                      value={z[key]}
                                      onChange={(e) =>
                                        aendern(z, { [key]: e.target.value })
                                      }
                                    />
                                  ) : (
                                    z[key] || "—"
                                  )}
                                </td>
                              ))}
                              <td>
                                {z.abrechnungContactId !== contactId ? (
                                  <button
                                    type="button"
                                    className="portfolio-link"
                                    onClick={() =>
                                      onKontaktOeffnen(z.abrechnungContactId)
                                    }
                                  >
                                    über {z.abrechnungName}
                                  </button>
                                ) : (
                                  <>
                                    {!x.sicht.kooperation && (
                                      <span>Direktanbindung</span>
                                    )}
                                    {schreibbar && z.bearbeitbar && (
                                      <button
                                        type="button"
                                        className="btn"
                                        onClick={() => void entfernen(z)}
                                      >
                                        Produkt entfernen
                                      </button>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                          {(eintraege.length === 0 ||
                            (schreibbar && produkt.active !== false)) && (
                            <tr>
                              <td>{produkt.name}</td>
                              <td
                                colSpan={
                                  PORTFOLIO_TEXTFELDER.length +
                                  (x.sicht.kooperation ? 2 : 1)
                                }
                              >
                                {schreibbar && produkt.active !== false ? (
                                  <button
                                    type="button"
                                    className="btn"
                                    aria-label={`Produkt hinzufügen: ${produkt.name}`}
                                    onClick={() => hinzufuegen(produkt)}
                                  >
                                    Produkt hinzufügen
                                  </button>
                                ) : (
                                  <span>Noch kein Produkt</span>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </section>
  );
});
